import { fromJS } from 'immutable';

export const updateExistingModifier = (modifiers, modifierToUpdate, values, comboGroup) => {
  const previousEntry = modifiers.get(modifierToUpdate);
  if (!previousEntry) return modifiers; // defensive — caller passed invalid index
  const previousValueIds = previousEntry
    .get('values')
    .map(v => v.get('id'))
    .toSet();
  const newValueIds = fromJS(values)
    .map(v => v.get('id'))
    .toSet();
  const removedValueIds = previousValueIds.subtract(newValueIds);

  let updated =
    values.length === 0
      ? modifiers.remove(modifierToUpdate)
      : modifiers.setIn([modifierToUpdate, 'values'], fromJS(values));

  if (comboGroup) {
    updated = updated.filter(m => m.getIn(['modifier', 'parent_combo_group_id']) !== comboGroup).toList();
  }

  if (!removedValueIds.isEmpty()) {
    updated = updated
      .filterNot(entry => {
        const parentValueId = entry.getIn(['modifier', 'parent_modifier_value_id']);
        return parentValueId != null && removedValueIds.has(parentValueId);
      })
      .toList();
  }

  return updated;
};

/**
 * Inserts a selected modifier into the modifiers list. If the modifier is a child of a parent combo group,
 * inserts it after this parent.
 *
 */
export const insertNewModifier = (modifiers, group, comboGroup, selectedModifier, values) => {
  const parentComboGroupId = selectedModifier.get('parent_combo_group_id');
  const newModifier = fromJS({ group, combo_group: comboGroup, modifier: selectedModifier, values });

  if (parentComboGroupId) {
    const parentIndex = modifiers.findIndex(m => m.get('combo_group') === parentComboGroupId);
    if (parentIndex !== -1) {
      return modifiers.insert(parentIndex + 1, newModifier);
    }
  }
  return modifiers.push(newModifier);
};

export const getSelectedModifierValues = (modifiers, modifier) => {
  const groupId = modifier.get('groupId') ?? null;
  const comboGroupId = modifier.get('combo_group_id') ?? null;

  let modifierState;
  if (groupId !== null) {
    modifierState = modifiers.find(
      m => m.get('group') === groupId && m.getIn(['modifier', 'id']) === modifier.get('id')
    );
  } else if (comboGroupId !== null) {
    modifierState = modifiers.find(
      m => m.get('combo_group') === comboGroupId && m.getIn(['modifier', 'id']) === modifier.get('id')
    );
  }
  return modifierState ? modifierState.get('values').toJS() : [];
};

export const transformModifierValues = value => (typeof value === 'string' ? [{ value }] : value);

/**
 * For each chosen value with use_product_modifiers, seed nested default modifier
 * entries from the value's product if no nested entries for that value already
 * exist in `modifiers`. Idempotent — safe to call after every selection.
 *
 * `getDefaultsForProduct(product)` is a callback returning an Immutable List of
 * default modifier entries (the result of selectDefaultModifiers, which lives
 * in selectors/browse — passed as a callback to avoid an import cycle).
 */
export const seedNestedDefaultsForValues = (modifiers, values, getDefaultsForProduct) => {
  let result = modifiers;
  values.forEach(value => {
    if (!value.get('use_product_modifiers') || !value.get('product')) return;
    const valueId = value.get('id');
    const alreadyHasNested = result.some(
      m => m.getIn(['modifier', 'parent_modifier_value_id']) === valueId
    );
    if (alreadyHasNested) return;
    const nestedDefaults = getDefaultsForProduct(value.get('product'));
    if (!nestedDefaults || !nestedDefaults.size) return;
    const tagged = nestedDefaults.map(m =>
      m.setIn(['modifier', 'parent_modifier_value_id'], valueId)
    );
    result = result.concat(tagged);
  });
  return result;
};

/**
 * Removes nested modifier entries whose parent_modifier_value_id no longer
 * refers to a currently-chosen value.
 *
 * We deliberately do NOT filter chosen values by use_product_modifiers here.
 * After a cart round-trip the order API echo strips catalog metadata, so the
 * flag may be missing from persisted value objects. The presence of the value
 * id in any entry's values is sufficient to keep the nested entry — the
 * selector decides whether to surface a nested page using the canonical
 * product catalog data.
 *
 * Returns the same reference when nothing was pruned (cheap identity check
 * for callers).
 */
export const pruneStaleNestedModifierEntries = modifiers => {
  const liveValueIds = modifiers
    .flatMap(entry => entry.get('values').map(v => v.get('id')))
    .toSet();

  const cleaned = modifiers
    .filter(entry => {
      const parentId = entry.getIn(['modifier', 'parent_modifier_value_id']);
      if (!parentId) return true;
      return liveValueIds.has(parentId);
    })
    .toList();

  return cleaned.size !== modifiers.size ? cleaned : modifiers;
};
